import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
  QUESTION_TYPES,
  addQuestion,
  addSection,
  copyItinerary,
  draftProblems,
  guestScreens,
  moveInList,
  moveQuestion,
  moveSection,
  newItineraryItem,
  newId,
  removeQuestion,
  removeSection,
  setTourAttached,
  updateItinerary,
  updateQuestion,
  updateSection
} from './draft';

/**
 * Card 324 — the questionnaire builder.
 *
 * One long page rather than a wizard: the admin builds this once per season
 * and edits it rarely, and seeing the whole form at once is how they check it.
 * Every question is edited in English. French was removed on 2026-10-02 and
 * may come back: Localised still lays out several languages side by side for
 * that day, when a guest who switches language must never meet a blank label.
 */

export const CARD =
  'w-full bg-white rounded-3xl shadow-sm border border-solid border-slate-200 p-6 min-w-0';
const INPUT =
  'w-full text-sm text-slate-900 placeholder-slate-400 border border-solid border-slate-300 rounded-xl px-3 py-2.5 focus:outline-none focus:border-indigo-500 bg-white';
const SMALL_BUTTON =
  'text-xs font-bold rounded-lg px-2.5 py-1.5 cursor-pointer border border-solid border-slate-200 bg-white text-slate-600 hover:border-slate-300 disabled:opacity-40 disabled:cursor-default';
const LINK_BUTTON =
  'text-sm font-bold text-indigo-700 bg-transparent border-0 cursor-pointer p-0 hover:underline';
const PRIMARY =
  'bg-indigo-600 hover:bg-indigo-700 active:translate-y-px disabled:opacity-60 text-white text-sm font-bold border-0 rounded-xl px-4 py-2.5 cursor-pointer transition-colors';

const LANG_LABEL = { en: 'English', fr: 'French' };

/** One piece of text in every enabled language, side by side on wide screens. */
const Localised = ({ label, value, onChange, locales, multiline, placeholder, testId }) => (
  <div className={`grid gap-2 ${locales.length > 1 ? 'md:grid-cols-2' : ''}`}>
    {locales.map(locale => {
      const Field = multiline ? 'textarea' : 'input';
      return (
        <label key={locale} className="block min-w-0">
          <span className="block text-xs font-bold text-slate-500 mb-1">
            {label}
            {locales.length > 1 ? ` (${LANG_LABEL[locale]})` : ''}
          </span>
          <Field
            className={`${INPUT} ${multiline ? 'min-h-[72px] resize-y' : ''}`}
            value={(value && value[locale]) || ''}
            placeholder={placeholder}
            lang={locale}
            onChange={e => onChange({ ...value, [locale]: e.target.value })}
            data-testid={testId ? `${testId}-${locale}` : undefined}
          />
        </label>
      );
    })}
  </div>
);

Localised.propTypes = {
  label: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func,
  locales: PropTypes.array,
  multiline: PropTypes.bool,
  placeholder: PropTypes.string,
  testId: PropTypes.string
};

const QuestionEditor = ({ question, index, count, locales, onChange, onMove, onRemove }) => {
  const choice = question.type === 'single' || question.type === 'multi';
  const setOptions = options => onChange({ options });
  return (
    <div
      className="rounded-2xl border border-solid border-slate-200 bg-slate-50/60 p-4"
      data-testid={`question-${question.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select
          className={`${INPUT} !w-auto`}
          value={question.type}
          onChange={e => onChange({ type: e.target.value })}
          aria-label="Question type"
          data-testid={`question-type-${question.id}`}>
          {QUESTION_TYPES.map(t => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        {question.type === 'rating5' && (
          <label className="flex items-center gap-1.5 text-xs font-bold text-slate-700 cursor-pointer">
            <input
              type="radio"
              name="overall-question"
              checked={!!question.overall}
              onChange={() => onChange({ overall: true })}
              className="accent-indigo-600"
              data-testid={`question-overall-${question.id}`}
            />
            Overall score
          </label>
        )}
        {question.type === 'yesno' && (
          <label className="flex items-center gap-1.5 text-xs font-bold text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={!!question.recommend}
              onChange={e => onChange({ recommend: e.target.checked })}
              className="accent-indigo-600"
            />
            &ldquo;Would recommend&rdquo; score
          </label>
        )}
        {!question.overall && (
          <label className="flex items-center gap-1.5 text-xs font-bold text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={!!question.required}
              onChange={e => onChange({ required: e.target.checked })}
              className="accent-indigo-600"
            />
            Required
          </label>
        )}
        <span className="flex-1" />
        <button
          type="button"
          className={SMALL_BUTTON}
          disabled={index === 0}
          onClick={() => onMove(-1)}
          aria-label="Move question up">
          Up
        </button>
        <button
          type="button"
          className={SMALL_BUTTON}
          disabled={index === count - 1}
          onClick={() => onMove(1)}
          aria-label="Move question down">
          Down
        </button>
        <button
          type="button"
          className={SMALL_BUTTON}
          onClick={onRemove}
          data-testid={`question-remove-${question.id}`}>
          Remove
        </button>
      </div>

      <Localised
        label="Question"
        value={question.label}
        locales={locales}
        onChange={label => onChange({ label })}
        testId={`question-label-${question.id}`}
      />

      {choice && (
        <div className="mt-3 flex flex-col gap-2">
          {(question.options || []).map((option, oi) => (
            <div key={option.id} className="flex items-end gap-2">
              <div className="flex-1 min-w-0">
                <Localised
                  label={`Choice ${oi + 1}`}
                  value={option.label}
                  locales={locales}
                  onChange={label =>
                    setOptions(
                      question.options.map(o => (o.id === option.id ? { ...o, label } : o))
                    )
                  }
                />
              </div>
              <button
                type="button"
                className={`${SMALL_BUTTON} mb-1`}
                disabled={question.options.length <= 2}
                onClick={() => setOptions(question.options.filter(o => o.id !== option.id))}>
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className={`${LINK_BUTTON} self-start`}
            onClick={() =>
              setOptions([
                ...(question.options || []),
                { id: newId('o'), label: { en: '', fr: '' } }
              ])
            }>
            Add a choice
          </button>
        </div>
      )}
    </div>
  );
};

QuestionEditor.propTypes = {
  question: PropTypes.object.isRequired,
  index: PropTypes.number,
  count: PropTypes.number,
  locales: PropTypes.array,
  onChange: PropTypes.func,
  onMove: PropTypes.func,
  onRemove: PropTypes.func
};

const ItineraryEditor = ({ tour, tourName, otherTours, locales, onChange, onCopyFrom }) => {
  const items = tour.itinerary;
  const setItem = (id, patch) => onChange(items.map(i => (i.id === id ? { ...i, ...patch } : i)));
  return (
    <div
      className="rounded-2xl border border-solid border-slate-200 p-4"
      data-testid={`itinerary-${tour.tourId}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h4 className="text-sm font-black text-slate-900 m-0">{tourName}</h4>
        {otherTours.length > 0 && items.length === 0 && (
          <select
            className={`${INPUT} !w-auto`}
            value=""
            onChange={e => e.target.value && onCopyFrom(e.target.value)}
            aria-label={`Copy hotels and experiences into ${tourName}`}>
            <option value="">Copy from another tour</option>
            {otherTours.map(t => (
              <option key={t.tourId} value={t.tourId}>
                {t.name}
              </option>
            ))}
          </select>
        )}
      </div>
      {items.length === 0 && (
        <p className="text-sm text-slate-500 m-0 mb-3">
          No hotels or experiences yet. Guests on this tour will only see the questions above.
        </p>
      )}
      <div className="flex flex-col gap-3">
        {items.map((item, index) => (
          <div
            key={item.id}
            className="rounded-xl bg-slate-50 p-3"
            data-testid={`itinerary-item-${item.id}`}>
            <div className="grid gap-2 md:grid-cols-[140px_1fr_1fr]">
              <select
                className={INPUT}
                value={item.kind}
                onChange={e => setItem(item.id, { kind: e.target.value })}
                aria-label="Hotel or experience">
                <option value="hotel">Hotel</option>
                <option value="experience">Experience</option>
              </select>
              <input
                className={INPUT}
                value={item.name}
                placeholder={
                  item.kind === 'hotel' ? 'Hotel name' : 'Experience, e.g. Cliffs of Moher'
                }
                onChange={e => setItem(item.id, { name: e.target.value })}
                aria-label="Name"
              />
              <input
                className={INPUT}
                value={item.place || ''}
                placeholder="Town or city"
                onChange={e => setItem(item.id, { place: e.target.value })}
                aria-label="Town or city"
              />
            </div>
            {item.kind !== 'hotel' && (
              <div className="mt-2">
                <Localised
                  label="What it is"
                  placeholder="e.g. Guided tour"
                  value={item.service}
                  locales={locales}
                  onChange={service => setItem(item.id, { service })}
                />
              </div>
            )}
            <div className="flex gap-2 mt-2 justify-end">
              <button
                type="button"
                className={SMALL_BUTTON}
                disabled={index === 0}
                onClick={() => onChange(moveInList(items, index, -1))}>
                Up
              </button>
              <button
                type="button"
                className={SMALL_BUTTON}
                disabled={index === items.length - 1}
                onClick={() => onChange(moveInList(items, index, 1))}>
                Down
              </button>
              <button
                type="button"
                className={SMALL_BUTTON}
                onClick={() => onChange(items.filter(i => i.id !== item.id))}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-4 mt-3">
        <button
          type="button"
          className={LINK_BUTTON}
          onClick={() => onChange([...items, newItineraryItem('hotel')])}>
          Add a hotel
        </button>
        <button
          type="button"
          className={LINK_BUTTON}
          onClick={() => onChange([...items, newItineraryItem('experience')])}>
          Add an experience
        </button>
      </div>
    </div>
  );
};

ItineraryEditor.propTypes = {
  tour: PropTypes.object.isRequired,
  tourName: PropTypes.string,
  otherTours: PropTypes.array,
  locales: PropTypes.array,
  onChange: PropTypes.func,
  onCopyFrom: PropTypes.func
};

const Editor = ({ initialDraft, tourOptions, onSave, onCancel, busy, error }) => {
  const [draft, setDraft] = useState(initialDraft);
  const [showProblems, setShowProblems] = useState(false);
  const locales = draft.locales;
  const problems = draftProblems(draft);
  const tourName = id => (tourOptions.find(t => t.id === id) || {}).name || 'Tour';

  const save = () => {
    if (problems.length) {
      setShowProblems(true);
      return;
    }
    onSave(draft);
  };

  return (
    <div className="flex flex-col gap-6" data-testid="questionnaire-editor">
      <section className={CARD}>
        <label className="block">
          <span className="block text-sm font-bold text-slate-900 mb-1">Name</span>
          <span className="block text-xs text-slate-500 mb-2">
            For your team only. Guests never see it.
          </span>
          <input
            className={`${INPUT} max-w-md`}
            value={draft.name}
            onChange={e => setDraft({ ...draft, name: e.target.value })}
            data-testid="questionnaire-name"
          />
        </label>

        {draft.hadFrench && (
          <p
            className="mt-5 text-sm text-amber-800 bg-amber-50 rounded-xl p-3 m-0"
            data-testid="questionnaire-french-dropped">
            This form also has French text from before. The builder is English only for now, so
            saving it will show guests the English version only.
          </p>
        )}

        <div className="mt-5 flex flex-col gap-4">
          <Localised
            label="Welcome text"
            placeholder="Optional. Leave empty for our standard welcome."
            value={draft.intro}
            locales={locales}
            multiline
            onChange={intro => setDraft({ ...draft, intro })}
          />
          <Localised
            label="Thank-you text"
            placeholder="Optional. Leave empty for our standard thank you."
            value={draft.thankYou}
            locales={locales}
            multiline
            onChange={thankYou => setDraft({ ...draft, thankYou })}
          />
        </div>
      </section>

      {draft.sections.map((section, si) => (
        <section key={section.id} className={CARD} data-testid={`section-${section.id}`}>
          <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="text-base font-black text-slate-900 m-0">Section {si + 1}</h3>
            <div className="flex gap-2">
              <button
                type="button"
                className={SMALL_BUTTON}
                disabled={si === 0}
                onClick={() => setDraft(moveSection(draft, si, -1))}
                aria-label="Move section up">
                Up
              </button>
              <button
                type="button"
                className={SMALL_BUTTON}
                disabled={si === draft.sections.length - 1}
                onClick={() => setDraft(moveSection(draft, si, 1))}
                aria-label="Move section down">
                Down
              </button>
              <button
                type="button"
                className={SMALL_BUTTON}
                disabled={draft.sections.length === 1}
                onClick={() => setDraft(removeSection(draft, section.id))}>
                Remove section
              </button>
            </div>
          </div>
          <Localised
            label="Section heading"
            placeholder="Optional"
            value={section.title}
            locales={locales}
            onChange={title => setDraft(updateSection(draft, section.id, { title }))}
          />
          <div className="flex flex-col gap-3 mt-4">
            {section.questions.map((question, qi) => (
              <QuestionEditor
                key={question.id}
                question={question}
                index={qi}
                count={section.questions.length}
                locales={locales}
                onChange={patch => setDraft(updateQuestion(draft, question.id, patch))}
                onMove={delta => setDraft(moveQuestion(draft, section.id, qi, delta))}
                onRemove={() => setDraft(removeQuestion(draft, question.id))}
              />
            ))}
          </div>
          <select
            className={`${INPUT} !w-auto mt-4`}
            value=""
            onChange={e =>
              e.target.value && setDraft(addQuestion(draft, section.id, e.target.value))
            }
            aria-label="Add a question"
            data-testid={`section-add-${section.id}`}>
            <option value="">Add a question</option>
            {QUESTION_TYPES.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </section>
      ))}

      <button
        type="button"
        className={`${LINK_BUTTON} self-start`}
        onClick={() => setDraft(addSection(draft))}>
        Add a section
      </button>

      <section className={CARD}>
        <h3 className="text-base font-black text-slate-900 m-0">Tours</h3>
        <p className="text-sm text-slate-500 m-0 mt-1 mb-4 max-w-[68ch]">
          Guides on these tours show this questionnaire. Add each tour&rsquo;s hotels and
          experiences so guests can rate them one by one. A tour can only be on one active
          questionnaire.
        </p>
        {tourOptions.length === 0 && (
          <p className="text-sm text-slate-600 m-0">
            You have no tours yet. Add them under Setup, then come back to attach them.
          </p>
        )}
        <div className="flex flex-col gap-2 mb-4">
          {tourOptions.map(option => {
            const attached = draft.tours.some(t => t.tourId === option.id);
            const elsewhere =
              option.questionnaireId && option.questionnaireId !== draft.id
                ? option.questionnaireName
                : null;
            return (
              <label
                key={option.id}
                className={`flex items-center gap-2 text-sm ${
                  elsewhere ? 'text-slate-400' : 'text-slate-800 cursor-pointer'
                }`}>
                <input
                  type="checkbox"
                  checked={attached}
                  disabled={!!elsewhere}
                  onChange={e => setDraft(setTourAttached(draft, option.id, e.target.checked))}
                  className="accent-indigo-600"
                  data-testid={`tour-${option.id}`}
                />
                {option.name}
                {elsewhere && <span className="text-xs">(on &ldquo;{elsewhere}&rdquo;)</span>}
              </label>
            );
          })}
        </div>
        <div className="flex flex-col gap-4">
          {draft.tours.map(tour => (
            <ItineraryEditor
              key={tour.tourId}
              tour={tour}
              tourName={tourName(tour.tourId)}
              locales={locales}
              otherTours={draft.tours
                .filter(t => t.tourId !== tour.tourId && t.itinerary.length > 0)
                .map(t => ({ tourId: t.tourId, name: tourName(t.tourId) }))}
              onChange={itinerary => setDraft(updateItinerary(draft, tour.tourId, () => itinerary))}
              onCopyFrom={from => setDraft(copyItinerary(draft, from, tour.tourId))}
            />
          ))}
        </div>
      </section>

      <section className={CARD}>
        <h3 className="text-base font-black text-slate-900 m-0">What guests will see</h3>
        <ol
          className="text-sm text-slate-700 mt-3 mb-0 pl-5 flex flex-col gap-1"
          data-testid="guest-screens">
          {guestScreens(draft).map((screen, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{screen}</li>
          ))}
        </ol>
      </section>

      {(showProblems && problems.length > 0) || error ? (
        <div
          className="text-sm text-rose-700 bg-rose-50 border border-solid border-rose-200 rounded-xl p-3"
          role="alert"
          data-testid="editor-problems">
          {error && <p className="m-0">{error}</p>}
          {showProblems && problems.length > 0 && (
            <ul className="m-0 pl-5">
              {problems.map(problem => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      <div className="flex gap-3">
        <button
          type="button"
          className={PRIMARY}
          disabled={busy}
          onClick={save}
          data-testid="editor-save">
          {busy ? 'Saving' : 'Save'}
        </button>
        <button
          type="button"
          className="bg-transparent text-slate-600 text-sm font-bold border-0 cursor-pointer px-4"
          onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

Editor.propTypes = {
  initialDraft: PropTypes.object.isRequired,
  tourOptions: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  error: PropTypes.string
};

export default Editor;
